"use client";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useSearchParams, usePathname } from "next/navigation";
import MissingProfileFieldsList from "~/components/hosts/MissingProfileFieldsList";

import { Community } from "@prisma/client";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { RxArrowRight, RxCalendar } from "react-icons/rx";
import { z } from "zod";
import { scrapeLumaEvent } from "~/app/api/actions/scraper/external-event";
import { EventTemplate } from "~/app/community-management/[communitySlug]/event-templates/_components/EventTemplatesList";
import EditProfileModal from "~/components/profile-components/EditProfileModal";
import QuestionnaireModal from "~/components/questionnaire/QuestionnaireModal";
import { MultiSelect } from "~/components/shadcn/MultiSelect";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import TemplateModalSelectorList from "~/components/template-components/TemplateModalSelectorList";
import RiverLoading from "~/components/themed/RiverLoading";
import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import { MissingUserData } from "~/utils/user-helpers";
import { If } from "../Conditional";
import { Label } from "../shadcn/Label";
import { Button } from "../themed/Button";
import { DialogClose, DialogFooter } from "../themed/Dialog";
import { Input } from "../themed/Input";
import LoginModal from "../themed/LoginModal";
import Modal from "../themed/Modal";
import { SwitchField } from "../themed/Switch";
import { ProposeEventFormData, useCreateEvent } from "./useCreateEvent";

import { TagInput } from "~/components/TagInput";
import { ExternalEventModalContent } from "./propose/ExternalEventModalContent";
import { FormControl, FormField, FormItem } from "../shadcn/Form";
import { ErrorMessage } from "@hookform/error-message";
import Spinner from "~/components/themed/spinner";

const modalStepTitles = (step: EventCreationSteps, communityName: string) => {
    switch (step) {
        case "create-event":
            return `Submit Event for ${communityName}`;
        case "external-platform":
            return `Event from External Platform`;
        case "event-tags":
            return `Select Event Tags`;
        default:
            return "";
    }
};

const modalStepDescriptions = (step: EventCreationSteps) => {
    switch (step) {
        case "create-event":
            return "Submit your event to be reviewed by the community.";
        case "external-platform":
            return "Propose your event and await approval from the Community Managers.";
        case "event-tags":
            return "Propose your event and await approval from the Community Managers.";
        default:
            return "";
    }
};

type SimpleCommunity = Pick<
    Community,
    | "id"
    | "name"
    | "slug"
    | "image"
    | "defaultTags"
    | "forceUseOfTemplatedEvent"
> & {
    hostRequiredData?: PrismaJson.HostRequiredData | null;
};

type SimpleProps = {
    community: SimpleCommunity;
    missingData: MissingUserData[];
    selectedTemplate?: EventTemplate;
    setSelectedTemplate: (template: EventTemplate | undefined) => void;
    form: UseFormReturn<ProposeEventFormData>;
};

export const EventProposeModalContent = ({
    community,
    missingData,
    selectedTemplate,
    setSelectedTemplate,
    form,
}: SimpleProps) => {
    const { register } = form;

    // LUMA SUCKER
    // ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
    // ⣿⣿⡿⠛⣉⣠⣤⣤⣤⣤⣤⣤⣤⣈⣉⡛⠻⠿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
    // ⣿⡟⢀⣾⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣷⡶⠟⠛⢿⣿⣿⣿⣿⣿⣿⣿⣿⣿
    // ⣿⣇⠸⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣄⠀⠀⢻⣿⣿⣿⣿⣿⣿⣿⣿
    // ⣿⣿⣦⡈⠻⢿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣦⡀⠈⣿⣿⣿⣿⣿⣿⣿⣿
    // ⣿⣿⣿⣿⣶⣤⣙⠻⢿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣷⡤⢿⣿⣿⣿⣿⣿⣿⣿
    // ⣿⣿⣿⣿⣿⣿⣿⣷⣦⣌⠙⢿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡇⠸⣿⣿⣿⣿⣿⣿⣿
    // ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣷⣄⠙⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⢿⣿⣿⣿⣿⣿⣿
    // ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⡆⢸⣿⣿⣿⣿⣿⣿⣿⣿⡇⠸⣿⣿⣿⣿⣿⣿
    // ⣿⣿⣿⣟⣿⣿⣿⣿⣿⣿⡿⠿⢷⣼⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⢿⣿⣿⣿⣿⣿
    // ⣿⡟⠉⠉⠛⠛⠛⠛⠛⠿⠷⢦⣤⣄⣿⣿⣿⣿⣿⣿⣿⣿⣿⡇⠸⣿⣿⣿⣿⣿
    // ⣿⡇⠀⣀⣤⣴⣤⣄⠀⠀⠀⠀⠀⠉⢻⣿⣿⣿⣿⣿⣿⣿⣿⣿⠀⢿⣿⣿⣿⣿
    // ⣿⡇⢰⡟⠁⠀⠀⠙⣷⠀⠀⠀⠀⠀⢸⣿⣿⣿⣿⣿⣿⣿⣿⣿⠷⠿⣿⣿⣿⣿
    // ⣿⣿⣼⣧⠀⠀⠀⣠⣿⣀⣀⣀⣀⣀⣼⣿⣿⣿⣿⣿⣿⠿⠿⠿⠿⠿⠿⠿⢿⣿
    // ⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿
    useEffect(() => {
        const scrape = async () => {
            if (form.watch("externalEventUrl")?.includes("lu.ma")) {
                const data = await scrapeLumaEvent(
                    form.watch("externalEventUrl")!,
                );

                if (!data) return;

                const startTime = DateTime.fromJSDate(data.startTime!)
                    .setZone("UTC", { keepLocalTime: true })
                    .toJSDate();

                const endTime = data.endTime
                    ? DateTime.fromJSDate(data.endTime!)
                          .setZone("UTC", { keepLocalTime: true })
                          .toJSDate()
                    : undefined;

                form.setValue("name", data.name, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
                form.setValue("startTime", startTime);
                form.setValue(
                    "startTime24h",
                    DateTime.fromJSDate(startTime).toFormat("HH:mm"),
                );
                form.setValue("endTime", endTime);
                if (endTime) {
                    form.setValue(
                        "endTime24h",
                        DateTime.fromJSDate(endTime).toFormat("HH:mm"),
                    );
                }
                form.setValue("timezone", data.timezone);
                form.setValue("location", data.location);
                form.setValue("city", data.city);
                form.setValue("country", data.country);
                form.setValue("googleMapsPlaceId", data.googlePlace.id);
                form.setValue("googleMapsPlaceName", data.googlePlace.name);
                form.setValue("locationName", data.googlePlace.name);
                form.setValue(
                    "latitude",
                    Number(data.googlePlace.geometry.latitude),
                );
                form.setValue(
                    "longitude",
                    Number(data.googlePlace.geometry.longitude),
                );
                form.setValue("useGoogleMapsLocation", true);
            }
        };
        scrape();
    }, [form.watch("externalEventUrl")]);

    return (
        <>
            <div className="max-w-screen">
                <div className="flex flex-col justify-start gap-4 text-start">
                    <If condition={missingData.length === 0}>
                        <If condition={!community.forceUseOfTemplatedEvent}>
                            <div className="text-left text-[16px] font-normal leading-normal">
                                <div className="flex flex-col gap-3">
                                    <Label htmlFor="new-event-name">
                                        Event Name
                                    </Label>
                                    <Input
                                        {...register("name", {
                                            required: "Event name is required",
                                            maxLength: {
                                                value: 80,
                                                message:
                                                    "Event name must be less than 80 characters",
                                            },
                                            onChange: (e) => {
                                                form.trigger("name");
                                            },
                                        })}
                                        autoComplete="off"
                                        type="text"
                                        placeholder="The Grand Slam Society"
                                        className={cn(
                                            community.forceUseOfTemplatedEvent &&
                                                "pointer-events-none cursor-not-allowed text-opacity-70",
                                        )}
                                    />
                                    <ErrorMessage
                                        errors={form.formState.errors}
                                        name="name"
                                        render={({ message }) => (
                                            <p className="text-danger">
                                                {message}
                                            </p>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col justify-start gap-2 text-start">
                                <SwitchField
                                    label="External Event"
                                    subLabel="Link event from external platform (i.e Evenbrite, Luma, Partiful, etc.)"
                                    checked={form.watch("isExternalEvent")}
                                    onCheckedChange={(val) => {
                                        form.setValue("isExternalEvent", val);
                                        if (!val) {
                                            form.setValue(
                                                "externalEventUrl",
                                                undefined,
                                            );
                                        }
                                    }}
                                />
                                <If condition={!!form.watch("isExternalEvent")}>
                                    <div className="flex max-h-96 flex-col gap-4 overflow-y-auto">
                                        <div className="flex flex-col gap-2">
                                            <Label>Event URL</Label>
                                            <Input
                                                {...form.register(
                                                    "externalEventUrl",
                                                    {
                                                        validate: (val) =>
                                                            z
                                                                .string()
                                                                .url()
                                                                .optional()
                                                                .safeParse(val)
                                                                .success,
                                                    },
                                                )}
                                                type="text"
                                                placeholder="https://eventbrite.com/e/some-event"
                                            />
                                        </div>
                                        {form.formState.errors
                                            .externalEventUrl && (
                                            <p className="text-danger">
                                                Invalid URL
                                            </p>
                                        )}
                                    </div>
                                </If>
                            </div>
                        </If>

                        <TemplateModalSelectorList
                            communitySlug={community.slug}
                            selectedTemplate={selectedTemplate}
                            setSelectedTemplate={(t) => {
                                setSelectedTemplate(t);
                                if (t) {
                                    form.setValue("name", t.name, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                    form.setValue("isExternalEvent", false);
                                }
                            }}
                        />

                        <Link
                            className="max-w-max text-white underline"
                            href="https://www.getriver.io/articles/the-art-of-hosting"
                        >
                            See our full hosting guide
                        </Link>
                    </If>
                    <If condition={missingData.length !== 0}>
                        <>
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold">
                                    We'd love to know more about you before we
                                    process your host application.{" "}
                                </span>
                                Please fill out the following profile details:
                            </p>
                            <div className="flex flex-col justify-start text-start">
                                <div className="mt-4 text-left font-mono text-[12px] font-normal leading-normal">
                                    <MissingProfileFieldsList
                                        missingData={missingData}
                                    />
                                </div>
                            </div>
                        </>
                    </If>
                </div>
            </div>
        </>
    );
};

const PickEventTagsModalContent = ({
    community,
    form,
}: {
    community: SimpleCommunity;
    form: UseFormReturn<ProposeEventFormData>;
}) => {
    const mainTags = form.watch("tags") ?? [];
    const otherTags = form.watch("multiTagsSelected") ?? [];
    const tags = [...mainTags, ...otherTags];
    return (
        <div className="flex w-full flex-col gap-4">
            <div className="flex flex-col gap-2">
                {community.defaultTags?.map((tagObj, index) => {
                    return (
                        <span key={tagObj.name}>
                            <Label>
                                {tagObj.name}
                                {tagObj.isMultiple
                                    ? " (select all that apply)"
                                    : ""}
                            </Label>
                            <>
                                <If condition={!tagObj.isMultiple}>
                                    <FormField
                                        control={form.control}
                                        name={`tags.${index}`}
                                        rules={{
                                            required:
                                                tagObj.isRequired ?? false,
                                            validate: (val) =>
                                                val !== undefined,
                                        }}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Select
                                                        {...field}
                                                        onValueChange={(e) => {
                                                            field.onChange(e);
                                                        }}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue
                                                                placeholder={`Select ${tagObj.name}`}
                                                            />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectGroup>
                                                                {/* I hate typescript lol */}
                                                                {community.defaultTags &&
                                                                    tagObj.tags?.map(
                                                                        (
                                                                            tagName: string,
                                                                        ) => (
                                                                            <SelectItem
                                                                                key={
                                                                                    tagName
                                                                                }
                                                                                value={
                                                                                    tagName
                                                                                }
                                                                            >
                                                                                {
                                                                                    tagName
                                                                                }
                                                                            </SelectItem>
                                                                        ),
                                                                    )}
                                                            </SelectGroup>
                                                        </SelectContent>
                                                    </Select>
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </If>
                                <If condition={!!tagObj.isMultiple}>
                                    <MultiSelect
                                        options={tagObj.tags?.map((o) => ({
                                            label: o,
                                            value: o,
                                        }))}
                                        onChange={(e) => {
                                            // this is a little hacky, but simplifies a lot
                                            form.setValue(
                                                "multiTagsSelected",
                                                e,
                                            );
                                        }}
                                    />
                                </If>
                            </>
                        </span>
                    );
                })}
                <TagInput
                    disabled={!!community.defaultTags.length}
                    tags={tags?.filter((e) => e !== undefined) ?? []}
                    onAddTag={(e) => {
                        if (community.defaultTags.length) return;
                        form.setValue("tags", [...tags, ...e]);
                    }}
                    onRemoveTag={(e) => {
                        if (community.defaultTags.length) return;
                        form.setValue(
                            "tags",
                            tags?.filter((tag) => tag !== e),
                        );
                    }}
                />
            </div>
        </div>
    );
};

const SubmittedEventModalContent = () => {
    return (
        <div className="flex flex-col items-center gap-4">
            <div className="relative flex h-[90px] w-full max-w-[90px] items-center justify-center">
                <Spinner type="large" />
            </div>
            <div className="flex flex-col gap-4 text-v3-N-400">
                Generating your event, this may take a few moments
            </div>
        </div>
    );
};

const ChapterLeadAutoSubmitModalContent = () => {
    return (
        <div className="flex flex-col items-center justify-center gap-6 py-8">
            <div className="relative flex h-[90px] w-full max-w-[90px] items-center justify-center">
                <Spinner type="large" />
            </div>

            <div className="flex flex-col items-center gap-3 text-center">
                <h3 className="text-lg font-medium text-white">
                    Creating Your Chapter Event
                </h3>
                <p className="max-w-md text-v3-N-400">
                    We're automatically creating an event using your chapter's
                    template. This will only take a moment.
                </p>
            </div>
        </div>
    );
};

export type EventCreationSteps =
    | "create-event"
    | "external-platform"
    | "event-tags"
    | "end-submitted"
    | "chapter-lead-auto-submit";

type Props = {
    community: SimpleCommunity;
    open: boolean;
    setOpen: (open: boolean) => void;
    onClose?: () => void;
    hasManagingPermissions?: boolean;
};
const EventProposeModal = (props: Props) => {
    const session = useSession();

    const [showQuestionnaireModal, setShowQuestionnaireModal] =
        useState<boolean>(false);
    const [questionnaireCompletionId, setQuestionnaireCompletionId] = useState<
        string | undefined
    >(undefined);
    const [showEditProfileModal, setShowEditProfileModal] = useState(false);

    const [steps, setSteps] = useState<EventCreationSteps[]>([]);

    const { data: isQuestionnaireRequired } =
        api.questionnaire.isQuestionnaireRequiredToProposeEvent.useQuery(
            {
                communityId: props.community.id,
            },
            {
                enabled: !!props.community.id && !!session.data?.user.id,
            },
        );

    const {
        communityRequiredData,
        missingData,
        onSubmit,
        onSubmitTemplate,
        selectedTemplate,
        setSelectedTemplate,
        form,
        handleChapterLeadTemplate,
    } = useCreateEvent({
        isOpen: props.open,
        mode: "propose",
        communitySlug: props.community.slug,
        questionnaireCompletionId,
        setSteps,
    });

    useEffect(() => {
        if (props.open) {
            handleChapterLeadTemplate();
        }
    }, [props.open]);

    useEffect(() => {
        if (
            isQuestionnaireRequired &&
            questionnaireCompletionId === undefined
        ) {
            setShowQuestionnaireModal(true);
        }
    }, [isQuestionnaireRequired, props.open]);

    const isExternalEvent = form.watch("isExternalEvent");

    useEffect(() => {
        const ret: EventCreationSteps[] = ["create-event"];
        if (isExternalEvent) {
            ret.push("external-platform");
        }
        if (props.community.defaultTags.length > 0) {
            ret.push("event-tags");
        }
        ret.push("end-submitted");
        setSteps(ret);
    }, [props.open, isExternalEvent]);

    useEffect(() => {
        // Don't set steps if we're in chapter lead auto-submit mode
        if (steps[0] === "chapter-lead-auto-submit") return;
    }, [steps]);

    if (props.open && !session.data?.user) {
        return (
            <LoginModal
                isOpen={true}
                setIsOpen={props.setOpen}
                title="Sign in to propose an event"
                description="You need to have a River account to propose events"
            />
        );
    }

    if (showQuestionnaireModal && props.open && !props.hasManagingPermissions) {
        return (
            <QuestionnaireModal
                isVisible={showQuestionnaireModal}
                onClose={() => props.setOpen(false)}
                setVisible={setShowQuestionnaireModal}
                onCompletion={(success, questionnaireCompletionId) => {
                    if (!success || !questionnaireCompletionId) {
                        // use the root modal as the close / open signal
                        // this blocks progression to further modals,
                        // even if you cancelled the questionnaire
                        props.setOpen(false);
                        setShowQuestionnaireModal(true);
                        setQuestionnaireCompletionId(undefined);
                    }
                    if (success) {
                        setShowQuestionnaireModal(false);
                        setQuestionnaireCompletionId(questionnaireCompletionId);
                    }
                }}
                questionnaire={isQuestionnaireRequired}
                action="propose event"
            />
        );
    }

    const onClose = () => {
        props.setOpen(false);
        setShowQuestionnaireModal(false);
        setQuestionnaireCompletionId(undefined);
        setSelectedTemplate(undefined);
        form.reset({
            name: undefined,
            tags: undefined,
            isExternalEvent: undefined,
            externalEventUrl: undefined,
        });
        // URL param will be updated by the useEffect above
    };

    return (
        <>
            <Modal
                open={props.open}
                onOpenChange={(v) => {
                    if (v === false) onClose();
                    props.setOpen(v);
                }}
                className="max-h-[70vh]"
                title={modalStepTitles(steps[0]!, props.community.name)}
                description={modalStepDescriptions(steps[0]!)}
                icon={<RxCalendar className="text-[24px]" />}
                footer={
                    <NextStepButton
                        form={form}
                        community={props.community}
                        missingData={missingData}
                        selectedTemplate={selectedTemplate}
                        onSubmitTemplate={onSubmitTemplate}
                        setOpen={props.setOpen}
                        steps={steps}
                        setSteps={setSteps}
                        onOpenEditProfile={() => {
                            setShowEditProfileModal(true);
                        }}
                        onSubmit={() => {
                            form.handleSubmit(onSubmit)();
                        }}
                    />
                }
            >
                <div>
                    <FormProvider {...form}>
                        <If condition={steps[0] === "create-event"}>
                            <EventProposeModalContent
                                form={form}
                                missingData={missingData}
                                selectedTemplate={selectedTemplate}
                                setSelectedTemplate={setSelectedTemplate}
                                community={props.community}
                            />
                        </If>
                        <If condition={steps[0] === "external-platform"}>
                            <ExternalEventModalContent form={form} />
                        </If>
                        <If condition={steps[0] === "event-tags"}>
                            <PickEventTagsModalContent
                                community={props.community}
                                form={form}
                            />
                        </If>
                        <If condition={steps[0] === "end-submitted"}>
                            <SubmittedEventModalContent />
                        </If>
                        <If condition={steps[0] === "chapter-lead-auto-submit"}>
                            <ChapterLeadAutoSubmitModalContent />
                        </If>
                    </FormProvider>
                </div>
            </Modal>
            {showEditProfileModal && (
                <EditProfileModal
                    isVisible={showEditProfileModal}
                    requiredData={communityRequiredData}
                    setVisible={(e) => {
                        setShowEditProfileModal(e);
                    }}
                />
            )}
        </>
    );
};

//This is dogshit but will have to do for now, no time to do a generic step API yet :/
const NextStepButton = ({
    form,
    missingData,
    community,
    steps,
    setSteps,
    selectedTemplate,
    onSubmitTemplate,
    onOpenEditProfile,
    setOpen,
    onSubmit,
}: {
    form: UseFormReturn<ProposeEventFormData>;
    missingData: MissingUserData[];
    community: SimpleCommunity;
    selectedTemplate?: EventTemplate | undefined;
    steps: EventCreationSteps[];
    setSteps: (steps: EventCreationSteps[]) => void;
    onSubmitTemplate: () => void;
    setOpen: (open: boolean) => void;
    onOpenEditProfile: () => void;
    onSubmit: () => void;
}) => {
    const [isValid, setIsValid] = useState(false);

    useEffect(() => {
        let isFormValid = form.formState.isValid;
        const requiredMultiTags = community.defaultTags?.filter(
            (t) => t.isRequired && t.isMultiple,
        );
        if (requiredMultiTags?.length && steps[0] === "event-tags") {
            const selectedTags = form.watch("multiTagsSelected") ?? [];
            isFormValid = isFormValid && selectedTags?.length > 0;
        }
        setIsValid(isFormValid);
    }, [form.formState.isValid, form.watch("multiTagsSelected")]);

    return (
        <DialogFooter className="flex flex-row items-center justify-end gap-2">
            <If condition={steps.length > 1}>
                <DialogClose asChild>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            form.reset();
                            setOpen(false);
                        }}
                    >
                        Nevermind
                    </Button>
                </DialogClose>
            </If>
            <If condition={missingData.length === 0}>
                <If condition={steps.length > 2}>
                    <Button
                        onClick={async (e) => {
                            e.preventDefault();
                            setSteps(steps.slice(1));
                        }}
                        disabled={!isValid}
                    >
                        Next <RxArrowRight />
                    </Button>
                </If>

                <If condition={steps.length === 2}>
                    <Button
                        onClick={async () => {
                            if (selectedTemplate !== undefined) {
                                onSubmitTemplate();
                            } else {
                                form.handleSubmit(onSubmit)();
                            }
                            setSteps(["end-submitted"]);
                        }}
                        disabled={!isValid}
                    >
                        Create Event
                    </Button>
                </If>
            </If>
            <If condition={missingData.length !== 0}>
                <Button
                    onClick={() => {
                        onOpenEditProfile();
                    }}
                >
                    Update profile
                </Button>
            </If>
        </DialogFooter>
    );
};

export default EventProposeModal;
